import { useToast } from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import { useMobileLayout } from '../../contexts/MobileLayoutContext';
import { isPhoneViewport } from '../../utils/phoneViewport';

// Layout constants matching MobileLayout
const BOTTOM_NAV_HEIGHT = 72;
const SAFE_AREA_BOTTOM_FALLBACK = 16;
/** Phone bottom offset that clears the bottom nav: the default when the nav
 * shows, and exported for a toast raised on an immersive page that lands on
 * one (Live End). */
export const PHONE_TOAST_ABOVE_NAV_PX = BOTTOM_NAV_HEIGHT + SAFE_AREA_BOTTOM_FALLBACK;

// Mobile gets longer durations — smaller screens, multitasking, harder to re-trigger
const MOBILE_DURATION = 5000;
const MOBILE_ERROR_DURATION = 8000;
const DESKTOP_DURATION = 3000;
const DESKTOP_ERROR_DURATION = 5000;

/**
 * Module-level dedupe ledger: collapses bursts of toasts with the same
 * description into a single toast. Common case: an API call rejects with
 * a server error message, the API interceptor raises a global toast
 * (e.g. ViewerForbiddenToastBridge), and the feature's own catch block
 * raises a second toast carrying the same message. The user only needs
 * to see one. Same-description toasts within `DEDUPE_WINDOW_MS` are
 * dropped silently. Different descriptions still fire.
 */
const DEDUPE_WINDOW_MS = 2000;
const recentToastDescriptions = new Map<string, number>();
function shouldDedupeDescription(description: string | undefined): boolean {
  if (!description) return false;
  const now = Date.now();
  const last = recentToastDescriptions.get(description);
  // Cheap GC: drop entries older than the window so the map can't grow
  // unbounded over a long session.
  for (const [key, ts] of recentToastDescriptions) {
    if (now - ts > DEDUPE_WINDOW_MS) recentToastDescriptions.delete(key);
  }
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return true;
  recentToastDescriptions.set(description, now);
  return false;
}

/**
 * Custom toast hook with application-wide defaults.
 *
 * Mobile-aware: on mobile devices, toasts are positioned at the bottom center
 * (away from edge gesture zones), offset above the bottom nav, with longer
 * durations since mobile users need more time to notice and interact.
 *
 * The viewport is read when a toast fires, never subscribed to. This hook is
 * called from `AuthenticatedApp` and ~100 components, and `useMobileDetection`
 * re-renders its caller on every breakpoint change: from the app root that was
 * a dozen whole-tree renders per breakpoint a window resize crossed (a 0.4 to
 * 1.9 s freeze measured on the dashboard).
 *
 * Also carries Chakra's `isActive` and `close`, for a caller that manages one
 * toast by id (a loading toast it later replaces), so no caller needs the raw
 * Chakra hook and its default placement.
 */
export const useAppToast = () => {
  const chakraToast = useToast();
  const { isImmersive } = useMobileLayout();

  const show = useCallback(
    (options: Parameters<typeof chakraToast>[0]) => {
      // Suppress duplicate descriptions within the dedupe window. The
      // most common trigger is a global toast (e.g. ViewerForbiddenToastBridge)
      // racing with a feature-specific catch block that surfaces the
      // same backend error message — the user sees two toasts saying
      // the same thing.
      const description =
        typeof options?.description === 'string' ? options.description : undefined;
      if (shouldDedupeDescription(description)) return undefined;

      const isMobile = isPhoneViewport();
      // Bottom offset: clears the nav bar when visible, just safe area when immersive
      const mobileBottomOffset = isImmersive ? SAFE_AREA_BOTTOM_FALLBACK : PHONE_TOAST_ABOVE_NAV_PX;
      const isError = options?.status === 'error';
      const mobileDuration = isError ? MOBILE_ERROR_DURATION : MOBILE_DURATION;
      const desktopDuration = isError ? DESKTOP_ERROR_DURATION : DESKTOP_DURATION;
      const duration = isMobile ? mobileDuration : desktopDuration;

      // Mobile-specific overrides
      const mobileOverrides = isMobile
        ? {
            position: 'bottom' as const,
            duration,
            containerStyle: {
              marginBottom: `${mobileBottomOffset}px`,
              maxWidth: '95vw',
              width: '95vw',
            },
          }
        : {};

      return chakraToast({
        // Desktop defaults
        position: 'bottom-right',
        variant: 'left-accent',
        isClosable: true,
        duration,
        // Mobile overrides (no-op on desktop)
        ...mobileOverrides,
        // Per-call overrides
        ...options,
        // Merge containerStyle so per-call styles don't wipe mobile positioning
        ...(mobileOverrides.containerStyle || options?.containerStyle
          ? {
              containerStyle: {
                ...mobileOverrides.containerStyle,
                ...options?.containerStyle,
              },
            }
          : {}),
      });
    },
    [chakraToast, isImmersive],
  );

  return useMemo(
    () => Object.assign(show, { isActive: chakraToast.isActive, close: chakraToast.close }),
    [show, chakraToast],
  );
};
